<template>
  <main>
    <div class="row">
      <div class="col-12">
        <div class="tile">
          <div class="form-inline">
            <b-form-input class="mr-1 mb-1" v-model="filterFormModel.keyword" v-on:keydown.enter="load"
                          :placeholder="$t('keyword')"></b-form-input>
            <client-select v-if="!client" class="mr-1 mb-1" :model="filterFormModel" attribute="client"
                           @change="load"></client-select>
            <form-select :model="filterFormModel" class="mr-1 mb-1" attribute="type" :options="[
              { value: null, text: $t('selectCustomerType') },
              { value: 'personal', text: $t('personal') },
              { value: 'enterprise', text: $t('enterprise') },
            ]"/>
            <time-range-picker :model="filterFormModel" :label="$t('createTime')"
                               class="mr-1 mb-1"></time-range-picker>
            <button @click="load" class="btn btn-primary mr-1 mb-1">{{ $t('search') }}</button>
            <button @click="reset" class="btn btn-secondary mr-1 mb-1">Reset</button>
          </div>
        </div>
      </div>
      <div class="col-12">
        <div class="c-sorting">
          <div class="row">
            <div class="col-lg-12 c-sorting__right">
              <div class="c-sorting__btn">
                <contract-export :filter="filterFormModel" :total-count="itemCount"
                                 v-if="['owner', 'admin', 'tech'].includes($store.state.user.role)"></contract-export>
              </div>
            </div>
          </div>
        </div>

        <div class="c-paging clearfix">
          <div class="c-paging__count" v-if="itemCount">
            {{ $t('found', [itemCount]) }}
          </div>
          <div class="c-paging__right">
            <div class="c-paging__numbers">
              <b-pagination v-if="itemCount > filterFormModel.pageSize" class="pull-right" size="sm"
                            v-model="filterFormModel.page" :total-rows="itemCount" :per-page="filterFormModel.pageSize"
                            @input="load"></b-pagination>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12" v-if="items.length">
        <div class="c-smart-table tile">
          <div class="row c-smart-table__title">
            <div class="col-lg-3">{{ $t('client') }}</div>
            <div class="col-lg-3">{{ $t('kyc') }}</div>
            <div class="col-lg-2">{{ $t('exportInfo') }}</div>
            <div class="col-lg-2">{{ $t('contractInfo') }}</div>
            <div class="col-lg-2"></div>
          </div>
          <div class="row" v-for="item in items" :key="item._id">
            <div class="col-lg-3">
              <div v-if="item.client">
                <p>
                  <b class="c-smart-table__label">{{ $t('client') }}: </b>
                  {{ item.client.companyName }}
                </p>
                <p>({{ item.client.domain }})</p>
              </div>
              <div v-else class="text-secondary small">{{ $t('unknown') }}</div>
            </div>
            <div class="col-lg-3">
              <p>
                <b class="c-smart-table__label">{{ $t('type') }}: </b>
                <span v-if="item.type === 'enterprise'" class="badge badge-primary">{{ $t(item.type) }}</span>
                <span v-if="item.type === 'personal'" class="badge badge-secondary">{{ $t(item.type) }}</span>
              </p>
              <p><b>{{ $t('createTime') }}: </b>{{ item.createTime | moment('DD/MM/YY HH:mm:ss') }}</p>
              <p>
                <b>{{ $t('createBy') }}: </b>
                <span v-if="item.createdBy">{{ item.createdBy.name }} ({{ item.createdBy.email }})</span>
                <span v-else class="text-secondary small">{{ $t('notUpdated') }}</span>
              </p>
            </div>
            <div class="col-lg-2">
              <p>
                <b>{{ $t('contactName') }}: </b>
                <span v-if="item.name">{{ item.name }}</span>
                <span v-else class="text-secondary small">{{ $t('notUpdated') }}</span>
              </p>
              <p>
                <b>{{ $t('address') }}: </b>
                <span v-if="item.address">{{ item.address }}</span>
                <span v-else class="text-secondary small">{{ $t('notUpdated') }}</span>
              </p>
              <p><b>{{ $t('taxCode') }}: </b>{{ item.taxCode }}</p>
            </div>
            <div class="col-lg-2">
              <p><b>{{ $t('confirmTime') }}: </b>
                <span v-if="item.confirmTime !== 0">{{ item.confirmTime | moment('DD/MM/YY HH:mm:ss') }}</span>
                <span v-else class="text-secondary small">{{ $t('notUpdated') }}</span>
              </p>
              <p>
                <b>{{ $t('confirmedBy') }}: </b>
                <span v-if="item.confirmedBy && item.confirmedBy.name">{{ item.confirmedBy.name }}</span>
                <span v-else class="text-secondary small">{{ $t('notUpdated') }}</span>
              </p>
              <p>
                <b>{{ $t('note') }}: </b>
                <span v-if="item.note">{{ item.note }}</span>
                <span v-else class="text-secondary small">{{ $t('notUpdated') }}</span>
              </p>
            </div>
            <div class="col-lg-2">
              <div class="c-smart-table__content">
                <div class="c-buttons clearfix is-2">
                  <div class="c-buttons__col">
                    <button
                        v-if="['owner', 'tech', 'operationsManager', 'account', 'manager', 'sales', 'kpiSales', 'am', 'accountant'].includes($store.state.user.role)"
                        class="btn btn-sm btn-info ml-1" @click="$refs.kycForm.update(item._id)">
                      <font-awesome-icon icon="edit"/>&nbsp; {{ $t('profile') }}
                    </button>
                    <button
                        v-if="!item.sendContractTime && ['owner', 'tech', 'operationsManager', 'account', 'accountant'].includes($store.state.user.role)"
                        class="btn btn-sm btn-primary ml-1"
                        @click="$refs.sendContract.update(`${item._id}?populateClient=true`)">
                      <font-awesome-icon icon="file"/>&nbsp;{{ $t('contract') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12 text-center text-danger" v-if="!items.length">
        <span v-if="filterFormModel.page === 1 && !filterFormModel.keyword">{{ $t('notFoundCreate') }}</span>
        <span v-if="filterFormModel.page > 1 || filterFormModel.keyword">{{ $t('notFoundSearch') }}</span>
      </div>

      <div class="col-12" v-if="itemCount">
        <div class="c-paging__count" v-if="itemCount">
          {{ $t('found', [itemCount]) }}
        </div>
        <b-pagination v-if="itemCount > filterFormModel.pageSize" class="pull-right" size="sm"
                      v-model="filterFormModel.page" :total-rows="itemCount" :per-page="filterFormModel.pageSize"
                      @input="load">
        </b-pagination>
      </div>
      <k-y-c-form ref="kycForm"></k-y-c-form>
      <send-contract ref="sendContract"></send-contract>
    </div>
  </main>
</template>

<script>
import List from '@/mixins/List';
import ClientSelect from "@/components/ClientSelect.vue";
import TimeRangePicker from "@/components/TimeRangePicker.vue";
import SendContract from "@/views/client/client-kyc/SendContract.vue";
import KYCForm from "@/views/client/client-kyc/KYCForm.vue";
import ContractExport from "@/views/client/client-kyc/ContractExport.vue";

export default {
  components: {
    ContractExport,
    KYCForm,
    SendContract,
    TimeRangePicker,
    ClientSelect
  },
  props: ['client'],
  mixins: [List],
  data() {
    return {
      service: '/client-kyc',
      reloadBus: 'reloadKYC',
      defaultFilterFormModel: {
        client: this.client ? this.client._id : null,
        keyword: null,
        type: null,
      },
    }
  },
  mounted() {
    this.load();
  },
  i18n: {
    messages: {
      en: {
        client: 'Client',
        kyc: 'KYC Information',
        exportInfo: 'Export Information',
        contractInfo: 'Contract Information',
        type: 'Type',
        createTime: 'Create Time',
        createBy: 'Create By',
        contactName: 'Contact Name',
        address: 'Address',
        taxCode: 'Tax Code',
        confirmedBy: 'Confirmed By',
        confirmTime: 'Confirm Time',
        note: 'Note',
        notFoundCreate: 'Not found any KYC information',
        notFoundSearch: 'Not found any KYC information',
        selectCustomerType: 'Select Customer Type',
        personal: 'Personal',
        enterprise: 'Enterprise',
        profile: "Profile",
        contract: "Contract",
        canceled: 'Canceled',
        confirmed: 'Confirmed',
        sentContract: 'Sent Contract',
      },
      vi: {
        client: 'Khách hàng',
        kyc: 'Thông tin KYC',
        exportInfo: 'Thông tin xuất hóa đơn',
        contractInfo: 'Thông tin hợp đồng',
        type: 'Loại khách hàng',
        createTime: 'Tạo lúc',
        createBy: 'Tạo bởi',
        contactName: 'Họ tên',
        address: 'Địa chỉ',
        taxCode: 'Mã số thuế',
        confirmedBy: 'Xác nhận bởi',
        confirmTime: 'Xác nhận lúc',
        note: 'Ghi chú',
        notFoundCreate: 'Không tìm thấy thông tin KYC',
        notFoundSearch: 'Không tìm thấy thông tin KYC',
        selectCustomerType: 'Chọn loại khách hàng',
        personal: "Cá nhân",
        enterprise: "Doanh nghiệp",
        profile: "Hồ sơ",
        contract: "Hợp đồng",
        canceled: 'Bị từ chối',
        confirmed: 'Đã xác nhận',
        sentContract: 'Hoàn thành hợp đồng',
      }
    }
  }
}
</script>